import { FHA, FHA_KEY } from '../../../constants/mapLayerIds';
import { FHA_FULL_SIZE, FHA_THUMBNAIL } from '../../../constants/imagery';
import { getLayerDatesBySeason } from './common';
import { hasFHA } from '../../productAssets';
import { scoutingIntervals } from '../../../config/FHALegends';

export function getImageUris(images = []) {
  const defaultImage = images[0];
  const imageUris = images.reduce(
    (_imageUris, image) => ({
      ..._imageUris,
      [image.meta.type]: image,
    }),
    {}
  );

  const image =
    imageUris[FHA_FULL_SIZE] || imageUris[FHA_THUMBNAIL] || defaultImage;
  const thumbnail =
    imageUris[FHA_THUMBNAIL] || imageUris[FHA_FULL_SIZE] || defaultImage;

  return {
    imageUri: image && image.uri,
    thumbnailUri: thumbnail && thumbnail.uri,
    northeastPoint: image && image.meta.bbox.northeast.coordinates.reverse(),
    southwestPoint: image && image.meta.bbox.southwest.coordinates.reverse(),
  };
}

export function extractFHALayers(seasonObject, fhaData, productAssets) {
  const { year: seasonYear } = seasonObject;
  const FHADates = getLayerDatesBySeason(fhaData, seasonYear);
  const shouldLoadFHA = hasFHA(productAssets);
  if (FHADates.length && shouldLoadFHA) {
    const subLayers = FHADates.map((date) => ({ date, ...fhaData[date] }));
    const initialState = {
      date: FHADates[0],
      layer: FHA.SCOUTING,
      segmentSelected: FHA.SCOUTING,
    };
    return {
      [FHA_KEY]: { subLayers, activeSubLayerIds: [initialState, initialState] },
    };
  }
  return {};
}

function extractFHAVegetation(event) {
  const { meta, payload } = event;
  const { imageUri, northeastPoint, southwestPoint, thumbnailUri } =
    getImageUris(payload);

  return {
    imageUri,
    northeastPoint,
    southwestPoint,
    thumbnailUri,
    colorScheme: meta['veg-color-scheme'],
    maskPercentage: meta['mask-percentages'].total,
    provider: meta?.['collection-id']
  };
}

function extractFHAScouting(event) {
  const { meta, payload } = event;
  const { imageUri, northeastPoint, southwestPoint, thumbnailUri } =
    getImageUris(payload);
  const numScoutingIntervals = scoutingIntervals.length;
  const percentages = meta.mdzs
    ? Array(numScoutingIntervals)
        .fill()
        .map(
          (_, i) => Math.round(meta.mdzs[`rank_${i + 1}_percent`] * 1000) / 10
        )
    : [];

  return {
    imageUri,
    northeastPoint,
    southwestPoint,
    thumbnailUri,
    lowPercent: percentages[0],
    percentages: percentages.reverse(), // reversing order for display purposes
    provider: meta?.['collection-id'],
  };
}

function extractFHATrueColor(event) {
  const { payload, meta } = event;
  const { imageUri, northeastPoint, southwestPoint } = getImageUris(payload);

  return {
    imageUri,
    northeastPoint,
    southwestPoint,
    provider: meta?.['collection-id'],
  };
}

function extractEvent(event) {
  switch (event.layer) {
    case FHA.SCOUTING:
      return extractFHAScouting(event);
    case FHA.VEGETATION:
      return extractFHAVegetation(event);
    case FHA.TRUE_COLOR:
      return extractFHATrueColor(event);
    default:
      return null;
  }
}

export function extractFHAEvents(events) {
  const fhaDates = events.reduce(
    (result, event) => ({
      ...result,
      [event.eventDate]: {
        ...result[event.eventDate],
        [event.layer]: extractEvent(event),
      },
    }),
    {}
  );
  // Remove dates that don't have scouting vegetation and true color.
  // and remove dates that don't have less than 70% of cloud coverage
  const validDates = Object.keys(fhaDates)
    .filter((date) => {
      const event = fhaDates[date];
      return (
        event[FHA.SCOUTING] &&
        event[FHA.VEGETATION] &&
        event[FHA.VEGETATION].maskPercentage < 0.7
      );
    })
    .sort((a, b) => new Date(b) - new Date(a));
  return validDates.reduce(
    (validEvents, date) => ({
      ...validEvents,
      [date]: fhaDates[date],
    }),
    {}
  );
}
